<template>
  <div class="tw:w-full">
    <ChangeCommissionLevelConfirmationModal
      :isOpen="isChangeCommissionLevelModalOpen"
      :currentCpa="campaignsData?.cpa"
      :newCpa="newCpa"
      @closeModal="closeChangeCommissionLevelModal"
    />
    <StatusBarHeader :status="getHeaderStatus" :campaignType="campaignType" />
    <div class="tw:mt-4 tw:flex tw:flex-wrap tw:lg:flex-nowrap tw:gap-x-3 tw:gap-y-3 tw:lg:gap-y-0 tw:items-stretch">
      <CampaignStatusCard
        :campaignType="campaignType"
        :unavailabilityRate="unavailabilityRate"
        :partnerStatus="partnerStatus"
        :campaignStatus="status"
        :budgetPercentage="budgetPercentage"
        :isActionable="true"
        :hasHint="true"
        :isSmall="false"
        id="status-bar-status-card"
      />
      <ConnectivityProviderCard
        ref="connectivityProviderCardRef"
        :currentPartner="currentRCPartner"
        :partnerStatus="partnerStatus"
        :unavailabilityRate="unavailabilityRate"
        :campaignStatus="status"
        :campaignType="campaignType"
        :budgetPercentage="budgetPercentage"
        :isActionable="true"
        :hasHint="true"
        id="status-bar-connectivity-provider"
      />
      <CampaignRateUnavailabilityCard
        v-if="campaignType === 'netCpa'"
        :unavailability="unavailability"
        :unavailabilityRate="unavailabilityRate"
        :hasHint="true"
        id="status-bar-rate-unavailability"
      />

      <CampaignBudgetCard
        v-if="campaignType === 'cpc'"
        :budgetPercentage="budgetPercentage"
        :budget="budget"
        :partnerStatus="partnerStatus"
        :campaignStatus="status"
        :isActionable="true"
        :hasHint="true"
      />
      <CampaignCommissionCard
        v-if="campaignType === 'netCpa'"
        :cpa="campaignsData.cpa"
        @setNewCpa="setNewCpa"
        @openChangeCommissionLevelModal="openChangeCommissionLevelModal"
        :isActionable="true"
        :hasHint="true"
        :partnerStatus="partnerStatus"
        :campaignStatus="status"
        :unavailabilityRate="unavailabilityRate"
        id="status-bar-commission-rate"
      />

      <CampaignTypeCard v-if="campaignType === 'cpc'" :campaignType="campaignType" :hasHint="true" />
    </div>
  </div>
</template>
<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import StatusBarHeader from '@/components/rateConnect/statusBar/StatusBarHeader.vue'
import CampaignStatusCard from '@/components/rateConnect/statusBar/CampaignStatusCard.vue'
import ConnectivityProviderCard from '@/components/rateConnect/statusBar/ConnectivityProviderCard.vue'
import CampaignTypeCard from '@/components/rateConnect/statusBar/CampaignTypeCard.vue'
import CampaignRateUnavailabilityCard from '@/components/rateConnect/statusBar/CampaignRateUnavailabilityCard.vue'
import CampaignCommissionCard from '@/components/rateConnect/statusBar/CampaignCommissionCard.vue'
import ChangeCommissionLevelConfirmationModal from '@/components/rateConnect/statusBar/ChangeCommissionLevelConfirmationModal.vue'
import CampaignBudgetCard from '@/components/rateConnect/statusBar/CampaignBudgetCard.vue'

import { selectedAccommodationId } from '@/layouts/queries'
import { useBudget, useCampaign, useCompetitivenessStats, usePartners } from '@/components/rateConnect/queries'
import { formatPercentage } from '@/utils/Utility.ts'
import { useSessionValues } from '@/composables/useSessionValues'

const connectivityProviderCardRef = ref(null)
const newCpa = ref(null)
const router = useRouter()
const route = useRoute()
const isChangeCommissionLevelModalOpen = ref(false)
const openChangeCommissionLevelModal = () => {
  isChangeCommissionLevelModalOpen.value = true
}
const closeChangeCommissionLevelModal = () => {
  isChangeCommissionLevelModalOpen.value = false
  newCpa.value = null
}
const setNewCpa = newCpaValue => {
  newCpa.value = newCpaValue
}

const { language } = useSessionValues()
const { campaignsData, currentPartner } = useCampaign(selectedAccommodationId)
const { partnersData } = usePartners(selectedAccommodationId)
const { competitivenessStats } = useCompetitivenessStats(selectedAccommodationId)
const campaignType = computed(() => campaignsData.value?.campaignType)
const { budget } = useBudget({ accommodationId: selectedAccommodationId, campaignType })
const currentRCPartner = computed(() => partnersData.value?.find(item => item.partnerId === currentPartner.value))
const status = computed(() => campaignsData.value?.status)

const partnerStatus = computed(() =>
  currentRCPartner.value?.partnerStatus === 5 && currentRCPartner.value?.connectionStatus === 5 ? 'active' : 'inactive'
)

const unavailabilityRate = computed(() => {
  if (competitivenessStats.value?.unavailabilityShare === null) {
    return '-'
  } else {
    const sum =
      Math.round(competitivenessStats.value?.beatShare) +
      Math.round(competitivenessStats.value?.meetShare) +
      Math.round(competitivenessStats.value?.loseShare) +
      Math.round(competitivenessStats.value?.unavailabilityShare)

    if (sum > 100) {
      return Math.round(competitivenessStats.value?.unavailabilityShare) - 1
    } else if (sum < 100) {
      return Math.round(competitivenessStats.value?.unavailabilityShare) + 1
    } else {
      return Math.round(competitivenessStats.value?.unavailabilityShare)
    }
  }
})

const unavailability = computed(() => {
  if (!isNaN(unavailabilityRate.value)) {
    return formatPercentage(language.value, unavailabilityRate.value, 0, 0)
  } else return '-'
})

const budgetPercentage = computed(() => {
  const percentage = Math.round((budget.value?.left / budget.value?.limit) * 100)
  return percentage
})

const getHeaderStatus = computed(() => {
  if (
    status.value !== 'ACTIVE' ||
    partnerStatus.value !== 'active' ||
    (unavailabilityRate.value === 100 && campaignType.value === 'netCpa') ||
    ((budgetPercentage.value === 0 || budget.value?.left <= 0) && campaignType.value === 'cpc')
  )
    return 'error'
  if (budgetPercentage.value > 0 && budgetPercentage.value < 30) return 'warning'
  else return 'success'
})

onMounted(async () => {
  if (route.query?.changePartner === 'true') {
    await nextTick()

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }, 0)

    setTimeout(() => {
      if (connectivityProviderCardRef.value) {
        const cardElement = connectivityProviderCardRef.value.$el
        if (cardElement) {
          cardElement.click()
        }
      }
    }, 150)

    router.replace({ query: {} })
  }
})
</script>
